import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { BoardRoleEnum } from "enums/BoardRoleEnum";
import { createLogger } from "logging";
import { useApi } from "providers/Api";
import { useBoard } from "providers/Board";
import { usePermissions } from "providers/Permissions";
import { useToasts } from "providers/Toasts";
import { useTranslations } from "providers/Translations";
import { useCallback, useMemo } from "react";

import { BoardUsersContext } from "./BoardUsersContext";

const logger = createLogger("BoardUsersProvider");

export const BoardUsersProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { boardApi } = useApi();
  const { board } = useBoard();
  const { currentUserEmail, isAdministrator } = usePermissions();
  const i18n = useTranslations();
  const toasts = useToasts();

  const boardId = board?.id;

  const { isLoading, refetch, data, error } = useQuery({
    queryKey: ["boardparticipants", boardId],
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      return boardApi.getBoardUsers(boardId, signal);
    },
    enabled: !!boardId,
  });

  const reloadBoardUsers = useCallback(async () => {
    const result = await refetch();
    if (result.data) {
      toasts.refreshSuccess(i18n.BoardRole, undefined);
    }
  }, [refetch, i18n, toasts]);

  const contextValue = useMemo(() => {
    const boardUsers = isLoading ? [] : (data ?? []);

    const isBoardOwner =
      isAdministrator ||
      boardUsers.some((x) => {
        logger.trace("isBoardOwner(): checking", x.userId);
        return `${x.userId}` === currentUserEmail && x.role === BoardRoleEnum.Owner;
      });

    const isBoardMember =
      isAdministrator ||
      boardUsers.some((x) => {
        logger.trace("isBoardMember(): checking", x.userId);
        return `${x.userId}` === currentUserEmail;
      });

    return {
      boardId,
      boardUsers,
      error: error as AxiosError | undefined,
      isLoading,
      isBoardOwner,
      isBoardMember,
      reloadBoardUsers,
    };
  }, [boardId, data, isLoading, error, isAdministrator, currentUserEmail, reloadBoardUsers]);

  return <BoardUsersContext.Provider value={contextValue}>{children}</BoardUsersContext.Provider>;
};
